import { useEffect, useState } from "react";
import type { PriceStrategy } from "@/lib/channels";
import type { ConnectorRow } from "@/lib/integrations";
import { storedPriceFormState } from "@/lib/listing-pricing";
import { connectorDefaultAmount, connectorPriceRuleFromConfig, connectorPricingPatch } from "@/lib/wizard-channel-price";
import { supabase } from "@/lib/supabase";

const inputCls =
  "w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500";

export function ConnectorDefaultPrice(props: {
  orgId: string;
  connector: ConnectorRow;
  onSaved: () => void;
}) {
  const current = connectorPriceRuleFromConfig(props.connector.config);
  const stored = storedPriceFormState(current.strategy, current.config);
  const [strategy, setStrategy] = useState<PriceStrategy>(stored.rule);
  const [amount, setAmount] = useState(stored.amount);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const next = connectorPriceRuleFromConfig(props.connector.config);
    const form = storedPriceFormState(next.strategy, next.config);
    setStrategy(form.rule);
    setAmount(form.amount);
  }, [props.connector.id, props.connector.config]);

  const save = async () => {
    setSaving(true);
    setError("");
    const parsed = connectorDefaultAmount(strategy, amount);
    if (parsed.ok === false) {
      setSaving(false);
      setError(parsed.error);
      return;
    }
    const { data, error: writeError } = await supabase.rpc("update_connector_merchant_config", {
      p_org_id: props.orgId,
      p_connector_id: props.connector.id,
      p_config_patch: connectorPricingPatch(strategy, parsed.amount),
    });
    setSaving(false);
    if (writeError) {
      setError(writeError.message);
      return;
    }
    if (!(data as { success?: boolean } | null)?.success) {
      setError((data as { error?: string } | null)?.error ?? "Could not save the default price.");
      return;
    }
    props.onSaved();
  };

  return (
    <div className="mt-3 pt-3 border-t border-slate-100">
      <p className="text-xs font-medium text-slate-500 mb-2">Default selling price</p>
      <div className="grid grid-cols-1 sm:grid-cols-[1fr_120px_auto] gap-2 items-center">
        <select aria-label={`${props.connector.name} default price rule`} value={strategy} onChange={(e) => setStrategy(e.target.value as PriceStrategy)} className={inputCls}>
          <option value="inherit">Base price</option>
          <option value="percentage_markup">Percentage (+% / −%)</option>
          <option value="fixed_markup">Fixed amount (+$ / −$)</option>
          <option value="fixed_override">Fixed price</option>
        </select>
        {strategy !== "inherit" ? (
          <input aria-label={`${props.connector.name} default price amount`} value={amount} onChange={(e) => setAmount(e.target.value)} className={inputCls} placeholder={strategy === "percentage_markup" ? "10" : "5.00"} />
        ) : <span />}
        <button type="button" onClick={() => void save()} disabled={saving} className="px-3 py-2 text-xs font-medium text-emerald-700 border border-emerald-200 rounded-lg hover:bg-emerald-50 disabled:opacity-50">
          {saving ? "Saving" : "Save default"}
        </button>
      </div>
      {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
    </div>
  );
}
